import { clsx } from "clsx";
import { type ElementType, type FC, forwardRef, type JSX, type ReactElement, type RefObject, useState } from "react";
import { type BaseFieldProps, type EventOrValueHandler, Field, type WrappedFieldProps } from "redux-form";
import { HStack, SymbolIcon, TriggerButton, TriggerButtonIntent } from "widgets";

import { FormError } from "@/ui-modules/core/form/layout/FormError.js";
import { FormInputGroup } from "@/ui-modules/core/form/layout/FormGroup.js";
import { FormInputHint } from "@/ui-modules/core/form/layout/FormInputHint.js";
import { FormInputWidget } from "@/ui-modules/core/form/layout/FormInputWidget.js";
import { FormLabel } from "@/ui-modules/core/form/layout/FormLabel.js";
import { FormWarning } from "@/ui-modules/core/form/layout/FormWarning.js";

import { fieldIsInvalid, fieldIsInWarnState } from "../../../app-utils/form.js";
import { charsLeftMessage } from "../../../app-utils/string.js";
import style from "./styles/FormWidget.module.scss";

export interface IInputFieldProps extends Partial<WrappedFieldProps> {
  componentClass?: ElementType; // 'input' | 'textarea'
  maxLength?: number;
  className?: string;
  placeholder?: string;
  label?: string | ReactElement;
  type?: string; // 'text' | 'password' | 'number' etc..: Only relevant if componentClass is 'input'
  handleChange?: EventOrValueHandler<any>;
  horizontalLayout?: boolean;
  forceWarnState?: string; // we can't pass the warning dynamically from an async validation with the current versino of redux-form !
  suffix?: string | ReactElement;
  tooltip?: string;
  disabled?: boolean;
  autoFocus?: boolean;
  autoComplete?: string;
  rows?: number; // for textarea
  inputRef?: any;
}

/**
 * Input field to be used in context of redux-form
 *
 * created on 1/23/2017
 */
const InputField: FC<IInputFieldProps> = (props) => {
  const message =
    props.meta.dirty && props.maxLength ? charsLeftMessage(props.maxLength, props.input.value.length, 5) : null;

  const { type, placeholder, maxLength, disabled, autoFocus, inputRef, autoComplete } = props;

  const inputProps = {
    ...props.input,
    type,
    placeholder,
    maxLength,
    disabled,
    autoFocus,
    inputRef,
    autoComplete,
    className: clsx(props.className, style.input_widget, { [style.input_error]: fieldIsInvalid(props) }),
    step: props.type === "number" ? "any" : null,
    onChange: (e: any) => {
      props.input.onChange(e);
      props.handleChange?.(e.target.value);
    },
    onBlur: (e: any) => {
      const t = e.target.value;
      props.input.onBlur(t?.trim());
    },
  };

  let formControl = <input {...inputProps} />;

  if (props.componentClass === "textarea") {
    formControl = <textarea {...inputProps} rows={props.rows ?? 5} />;
  }
  if (props.type === "number") {
    // prevent the mouse wheel event to modify the value
    formControl = (
      <input
        {...inputProps}
        onWheel={(e) => {
          e.preventDefault();
        }}
      />
    );
  }

  if (props.suffix) {
    formControl = (
      <HStack>
        {formControl}
        {props.suffix}
      </HStack>
    );
  }

  return (
    <FormInputGroup horizontalLayout={props.horizontalLayout}>
      {props.label && <FormLabel>{props.label}</FormLabel>}

      <FormInputWidget tooltip={props.tooltip}>
        <FormInputHint align={"end"}>{message}</FormInputHint>
        {formControl}
        {fieldIsInvalid(props) && <FormError>{props.meta.error}</FormError>}

        {(!!props.forceWarnState || fieldIsInWarnState(props)) && (
          <FormWarning>{props.forceWarnState || props.meta.warning}</FormWarning>
        )}
      </FormInputWidget>
    </FormInputGroup>
  );
};

export default InputField;

export function InputReduxFormField(props: IInputFieldProps & BaseFieldProps) {
  return <Field {...props} />;
}

// --- Password Input ---
interface IPasswordInputProps {
  name: string;
  autocomplete: "new-password" | "current-password" | "off";
  placeholder?: string;
  label: string;
  allowReadability?: boolean;
  validate?: BaseFieldProps["validate"];
}

function PasswordVisibility(props): JSX.Element {
  const { setReadability, isReadable } = props;
  return (
    <TriggerButton
      onPress={() => setReadability(!isReadable)}
      style={{ padding: "4px 10px 3px", height: 30 }}
      icon={<SymbolIcon icon={isReadable ? "visibility_off" : "visibility"} />}
      intent={TriggerButtonIntent.Outlined}
      label={`${isReadable ? "Hide" : "Show"} Password`}
    />
  );
}

function PasswordInput(props: IPasswordInputProps & WrappedFieldProps & { customRef: RefObject<any> }) {
  const [isReadable, setReadability] = useState<boolean>(false);

  return (
    <InputField
      {...props}
      label={props.label}
      type={isReadable ? "text" : "password"}
      suffix={props.allowReadability && <PasswordVisibility setReadability={setReadability} isReadable={isReadable} />}
      componentClass={"input"}
      autoComplete={props.autocomplete}
      inputRef={props.customRef}
    />
  );
}

export const PasswordInputField = forwardRef<any, IPasswordInputProps>(function PasswordInputField(props, ref) {
  return <Field {...props} customRef={ref} component={PasswordInput} normalize={(v: string) => v.trim()} />;
});
